import { RefObject, useEffect, useRef } from 'react';
import type { Canvas } from 'fabric';
import type {
  CanvasJSON,
  CreativityEditorHandle,
  FieldBinding,
  PieceModel,
  ResolutionConfig
} from '@doer/frontend-components/creativity-editor';
import { applyBindingsToJson } from '../binding';
import type { RoutedFormData } from '../../../types/templates';

type ComputeDefaults = (data: RoutedFormData, resolutionId?: string) => RoutedFormData;

type UseActiveResolutionLoaderParams = {
  editorRef: RefObject<CreativityEditorHandle | null>;
  editorReady: boolean;
  canvas: Canvas | null;
  activeResolution: ResolutionConfig;
  fieldBindings: FieldBinding[];
  formDataRef: RefObject<RoutedFormData>;
  computeDefaults: ComputeDefaults;
  getSnapshot: (resolutionId: string) => CanvasJSON | undefined;
  saveSnapshot: (resolutionId: string) => void;
  flushBindings: () => Promise<void>;
  enqueueBinding: (data: RoutedFormData) => void;
  getSavedModel?: (resolutionId: string) => PieceModel | undefined;
  onFirstLoad?: () => void;
};

/**
 * Drives the active-resolution lifecycle
 * Uses the saved snapshot when available; otherwise patches the layout with formData.
 */
export const useActiveResolutionLoader = ({
  editorRef,
  editorReady,
  canvas,
  activeResolution,
  fieldBindings,
  formDataRef,
  computeDefaults,
  getSnapshot,
  saveSnapshot,
  flushBindings,
  enqueueBinding,
  getSavedModel,
  onFirstLoad
}: UseActiveResolutionLoaderParams): void => {
  const prevResolutionIdRef = useRef<string | null>(null);
  const firstLoadDoneRef = useRef(false);

  useEffect(() => {
    if (!editorReady || !canvas) return;

    const prevId = prevResolutionIdRef.current;
    prevResolutionIdRef.current = activeResolution.id;

    const savedSnapshot = getSnapshot(activeResolution.id);
    const savedModel = savedSnapshot ? undefined : getSavedModel?.(activeResolution.id);
    const data = formDataRef.current;

    let cancelled = false;
    const markFirstLoad = () => {
      if (firstLoadDoneRef.current) return;
      firstLoadDoneRef.current = true;
      onFirstLoad?.();
    };
    (async () => {
      // Drain any in-flight bindings (still applying to the prev resolution's canvas) before snapshotting it.
      await flushBindings();
      if (cancelled) return;
      if (prevId && prevId !== activeResolution.id) {
        saveSnapshot(prevId);
      }

      // Reopen: render the saved model as-is; the binder must not repaint over it.
      if (savedModel) {
        await editorRef.current?.loadPiece(savedModel);
        if (!cancelled) markFirstLoad();
        return;
      }

      const defaults = computeDefaults(data, activeResolution.id);
      const jsonToLoad =
        savedSnapshot ?? applyBindingsToJson(activeResolution.layout, data, fieldBindings, activeResolution.id, defaults);
      await editorRef.current?.loadProject(jsonToLoad);
      if (cancelled) return;

      // Always re-apply bindings post-load
      enqueueBinding(data);
      markFirstLoad();
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formData excluded: incremental changes handled by handleFieldChange
  }, [
    editorReady,
    canvas,
    activeResolution.layout,
    activeResolution.id,
    fieldBindings,
    saveSnapshot,
    getSnapshot,
    getSavedModel,
    computeDefaults,
    flushBindings,
    enqueueBinding,
    onFirstLoad
  ]);
};
